import { createSlice } from '@reduxjs/toolkit';

import SDK from '../Components/SDK';
import { getUserIdFromJid } from '../Helpers/Utility';
import { clearState } from './clearSlice';

const SCHEDULED_PAGE_SIZE = 50;
// Bounds the paging loop, should a server keep answering hasMore.
const SCHEDULED_MAX_PAGES = 20;
const HTTP_OK = 200;

const EMPTY_LIST = { items: [], total: 0, isLoading: false, isLoaded: false, isFailed: false, departedIds: [] };

// byChatId holds each chat's own scheduled messages; composer the send time picked for the
// message being written; editing the scheduled message open in the composer, if any.
const initialState = {
    byChatId: {},
    composer: {},
    editing: null,
    noticeDismissedTotal: {},
};

// Only the newest request of a chat may write its list, so a slower older one is dropped.
const latestRequestByChat = {};

const listOf = (state, chatId) => {
    if (!state.byChatId[chatId]) state.byChatId[chatId] = { ...EMPTY_LIST };
    return state.byChatId[chatId];
};

const byScheduledTime = (a, b) => Number(a.scheduledAt) - Number(b.scheduledAt);

const scheduledMessagesSlice = createSlice({
    name: 'scheduledMessages',
    initialState,
    reducers: {
        setScheduledLoading(state, action) {
            const { chatId } = action.payload || {};
            if (!chatId) return;
            const scheduledList = listOf(state, chatId);
            scheduledList.isLoading = true;
            scheduledList.isFailed = false;
        },
        setScheduledLoadFailed(state, action) {
            const { chatId } = action.payload || {};
            if (!chatId) return;
            const scheduledList = listOf(state, chatId);
            scheduledList.isLoading = false;
            scheduledList.isFailed = true;
        },
        setScheduledList(state, action) {
            const { chatId, items = [], total } = action.payload || {};
            if (!chatId) return;
            const scheduledList = listOf(state, chatId);
            // Gone from the server list, not removed here: sent, or deleted on another device.
            const kept = new Set(items.map(entry => entry.msgId));
            const departed = scheduledList.items.map(entry => entry.msgId).filter(msgId => !kept.has(msgId));
            scheduledList.departedIds = [...new Set([...(scheduledList.departedIds || []), ...departed])];
            scheduledList.items = [...items].sort(byScheduledTime);
            scheduledList.total = Number.isFinite(Number(total)) ? Number(total) : items.length;
            scheduledList.isLoading = false;
            scheduledList.isLoaded = true;
            scheduledList.isFailed = false;
        },
        clearDepartedScheduled(state, action) {
            const { chatId, msgIds = [] } = action.payload || {};
            const scheduledList = chatId && state.byChatId[chatId];
            if (!scheduledList) return;
            const handled = new Set(msgIds);
            scheduledList.departedIds = (scheduledList.departedIds || []).filter(msgId => !handled.has(msgId));
        },
        setScheduledTotal(state, action) {
            const { chatId, total } = action.payload || {};
            if (!chatId) return;
            listOf(state, chatId).total = Number(total) || 0;
        },
        updateScheduledMessage(state, action) {
            const { chatId, msgId, changes = {} } = action.payload || {};
            const scheduledList = chatId && state.byChatId[chatId];
            const scheduledMessage = scheduledList?.items.find(entry => entry.msgId === msgId);
            if (!scheduledMessage) return;
            Object.assign(scheduledMessage, changes);
            scheduledList.items.sort(byScheduledTime);
        },
        removeScheduledMessages(state, action) {
            const { chatId, msgIds = [] } = action.payload || {};
            const scheduledList = chatId && state.byChatId[chatId];
            if (!scheduledList || !msgIds.length) return;
            const removed = new Set(msgIds);
            const remaining = scheduledList.items.filter(entry => !removed.has(entry.msgId));
            scheduledList.total = Math.max(0, scheduledList.total - (scheduledList.items.length - remaining.length));
            scheduledList.items = remaining;
        },
        setComposerSchedule(state, action) {
            const { chatId, key, at } = action.payload || {};
            if (!chatId) return;
            state.composer[chatId] = { key, at };
        },
        clearComposerSchedule(state, action) {
            delete state.composer[action.payload];
        },
        startScheduledEdit(state, action) {
            state.editing = action.payload || null;
        },
        endScheduledEdit(state) {
            state.editing = null;
        },
        hideScheduledNotice(state, action) {
            const { chatId, total } = action.payload || {};
            if (!chatId) return;
            state.noticeDismissedTotal[chatId] = total;
        },
    },
    extraReducers: builder => {
        builder.addCase(clearState, () => initialState);
    },
});

export const {
    setScheduledLoading,
    setScheduledLoadFailed,
    setScheduledList,
    clearDepartedScheduled,
    setScheduledTotal,
    updateScheduledMessage,
    removeScheduledMessages,
    setComposerSchedule,
    clearComposerSchedule,
    startScheduledEdit,
    endScheduledEdit,
    hideScheduledNotice,
} = scheduledMessagesSlice.actions;

export const selectScheduledList = (state, chatId) => state.scheduledMessages.byChatId[chatId] || EMPTY_LIST;

export const selectComposerSchedule = (state, chatId) => state.scheduledMessages.composer[chatId] || null;

export const selectScheduledEdit = (state, chatId) => {
    const { editing } = state.scheduledMessages;
    return editing && editing.chatId === chatId ? editing : null;
};

/**
 * Whether the "You have N scheduled message(s)" notice shows for a chat.
 *
 * A dismissal holds only while the count stays the one it was dismissed at.
 *
 * @param {object} state Redux state.
 * @param {string} chatId Conversation id.
 * @returns {boolean} True when there is something waiting and it was not dismissed.
 */
export const selectIsNoticeShown = (state, chatId) => {
    const { total } = selectScheduledList(state, chatId);
    return total > 0 && state.scheduledMessages.noticeDismissedTotal[chatId] !== total;
};

/**
 * Loads all of the caller's scheduled messages of one chat, earliest sending time first,
 * page by page. A newer call for the same chat wins over one still in flight.
 *
 * @param {string} chatJid Conversation JID: the peer for 1-1, the group JID for a group.
 * @returns {Promise<object|undefined>} The last SDK response, or undefined when superseded.
 */
export const fetchScheduledMessages = chatJid => async dispatch => {
    const chatId = getUserIdFromJid(chatJid);
    if (!chatId) return undefined;
    const requestId = (latestRequestByChat[chatId] || 0) + 1;
    latestRequestByChat[chatId] = requestId;
    const isLatest = () => latestRequestByChat[chatId] === requestId;

    dispatch(setScheduledLoading({ chatId }));
    const { response, items, total } = await loadAllPages(chatJid);
    if (!isLatest()) return undefined;
    if (response?.statusCode !== HTTP_OK) {
        dispatch(setScheduledLoadFailed({ chatId }));
        return response;
    }
    dispatch(setScheduledList({ chatId, items, total }));
    return response;
};

/**
 * Refreshes only the number of scheduled messages a chat holds, for its banner and row.
 *
 * @param {string} chatJid Conversation JID.
 */
export const fetchScheduledCount = chatJid => async dispatch => {
    const chatId = getUserIdFromJid(chatJid);
    if (!chatId) return;
    const response = await SDK.getScheduledMessageCount(chatJid);
    if (response?.statusCode === HTTP_OK) dispatch(setScheduledTotal({ chatId, total: response.data?.total }));
};

const loadAllPages = async chatJid => {
    const items = [];
    let cursor;
    let response;
    for (let page = 0; page < SCHEDULED_MAX_PAGES; page += 1) {
        response = await SDK.getScheduledMessages({ toJid: chatJid, size: SCHEDULED_PAGE_SIZE, cursor });
        if (response?.statusCode !== HTTP_OK) return { response, items, total: 0 };
        const { items: pageItems = [], hasMore, cursor: nextCursor } = response.data || {};
        items.push(...pageItems);
        if (!hasMore || !nextCursor) break;
        cursor = nextCursor;
    }
    return { response, items, total: response?.data?.total };
};

export default scheduledMessagesSlice.reducer;
